import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, HelpCircle, BookOpen, CreditCard, Lock, Video, Mail, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { trackAccordion, trackSearch, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  questions: FAQItem[];
}

export default function HelpCenterPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<number | null>(0);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);

  useScrollTracking('HelpCenter');

  useEffect(() => {
    trackPageView('HelpCenter');
  }, []);

  // Track category expansion
  const handleCategoryExpand = (index: number, categoryName: string) => {
    if (expandedCategory === index) {
      setExpandedCategory(null);
    } else {
      trackAccordion.categoryExpand(`category-${index}`, categoryName);
      setExpandedCategory(index);
    }
  };

  // Track question expansion
  const handleQuestionExpand = (questionId: string, questionText: string, category: string) => {
    if (expandedQuestion === questionId) {
      setExpandedQuestion(null);
    } else {
      trackAccordion.questionExpand(questionId, questionText, category);
      setExpandedQuestion(questionId);
    }
  };

  // Track search
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term.length >= 3) {
      trackSearch.submit(term, 'help-center', 0);
    }
  };

  const faqCategories: FAQCategory[] = [
    {
      title: 'Getting Started',
      icon: <BookOpen className="w-6 h-6" />,
      questions: [
        {
          question: 'How do I create an account?',
          answer: 'Click the "Sign Up" button in the top right corner of any page. Fill in your email, username, and password, then click "Create Account". You\'ll receive a confirmation email to verify your account.'
        },
        {
          question: 'What courses are available?',
          answer: 'Visit our Courses page to see all available courses and bundles. We offer courses on Prompt Engineering, AI Tool Mastery, and more. Each course includes detailed curriculum information and pricing.'
        },
        {
          question: 'How do I enroll in a course?',
          answer: 'Browse our Courses page, select the course you want, and click "Enroll Now". You\'ll be guided through a secure checkout process. Once payment is complete, you\'ll have immediate access to your course materials.'
        },
        {
          question: 'Can I preview courses before purchasing?',
          answer: 'Yes! Each course page includes a detailed curriculum overview, learning objectives, and sample content to help you make an informed decision before enrolling.'
        }
      ]
    },
    {
      title: 'Account & Login',
      icon: <Lock className="w-6 h-6" />,
      questions: [
        {
          question: 'I forgot my password. How do I reset it?',
          answer: 'Click "Forgot Password" on the sign-in page. Enter your email address, and we\'ll send you a password reset link. Follow the instructions in the email to create a new password.'
        },
        {
          question: 'How do I update my account information?',
          answer: 'Log in to your account and navigate to Dashboard > Settings. Here you can update your name, email, password, and other account details.'
        },
        {
          question: 'Can I change my email address?',
          answer: 'Yes, you can update your email in Dashboard > Settings. You\'ll need to verify the new email address before the change takes effect.'
        },
        {
          question: 'How do I delete my account?',
          answer: 'If you wish to delete your account, please contact our support team at support@ai-mastery-hub.com. We\'ll process your request and confirm deletion within 48 hours.'
        }
      ]
    },
    {
      title: 'Courses & Learning',
      icon: <Video className="w-6 h-6" />,
      questions: [
        {
          question: 'How long do I have access to a course?',
          answer: 'Once you enroll in a course, you have lifetime access! Learn at your own pace and revisit materials anytime you need a refresher.'
        },
        {
          question: 'Can I download course materials?',
          answer: 'Course videos are available for streaming within the platform. Some courses include downloadable resources like PDFs, worksheets, and templates that you can save for offline use. '
        },
        {
          question: 'What if I\'m not satisfied with a course?',
          answer: 'We offer dedicated support to help you succeed! Contact our team anytime at support@ai-mastery-hub.com and we will be happy to assist with any questions about the course materials.'
        },
        {
          question: 'Are there prerequisites for courses?',
          answer: 'Most of our courses are designed for beginners and require no prior experience. Specific prerequisites (if any) are listed on each course page under "Requirements".'
        },
        {
          question: 'How do I track my progress?',
          answer: 'Your dashboard shows your progress for each enrolled course. As you complete lessons, your progress bar updates automatically. You can also see which topics you\'ve completed and what\'s remaining.'
        },
        {
          question: 'Will I receive a certificate upon completion?',
          answer: 'Yes! Upon completing 100% of a course, you\'ll receive a certificate of completion that you can download and share on LinkedIn or your resume.'
        }
      ]
    },
    {
      title: 'Billing & Payments',
      icon: <CreditCard className="w-6 h-6" />,
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover) and debit cards through our secure payment processor, Stripe.'
        },
        {
          question: 'Is my payment information secure?',
          answer: 'Absolutely. We use Stripe, an industry-leading payment processor with bank-level security. We never store your credit card information on our servers.'
        },
        {
          question: 'Do you offer payment plans?',
          answer: 'Currently, we require full payment at the time of enrollment. However, we regularly offer bundle discounts that provide significant savings when you purchase multiple courses together.'
        },
        {
          question: 'Can I purchase courses as a gift?',
          answer: 'Gift purchases are not currently automated, but we can arrange this manually. Contact support@ai-mastery-hub.com with details about which course you\'d like to gift, and we\'ll assist you.'
        },
        {
          question: 'Will I receive an invoice or receipt?',
          answer: 'Yes, you\'ll receive an email receipt immediately after purchase. You can also access all your receipts from Dashboard > Billing.'
        }
      ]
    },
    {
      title: 'Technical Support',
      icon: <HelpCircle className="w-6 h-6" />,
      questions: [
        {
          question: 'Videos won\'t play. What should I do?',
          answer: 'First, try refreshing the page and ensuring you have a stable internet connection. If the issue persists, try clearing your browser cache or using a different browser. For continued issues, contact support@ai-mastery-hub.com.'
        },
        {
          question: 'What browsers are supported?',
          answer: 'Our platform works best on the latest versions of Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for the best experience.'
        },
        {
          question: 'Can I access courses on mobile devices?',
          answer: 'Yes! Our platform is fully responsive and works on smartphones and tablets. You can learn on the go from any device with an internet connection.'
        },
        {
          question: 'I\'m experiencing slow loading times. What can I do?',
          answer: 'Slow loading can be caused by internet connection speed, browser cache, or browser extensions. Try clearing your cache, disabling extensions, or switching to a different network. If problems persist, contact support.'
        },
        {
          question: 'My progress isn\'t saving. Help!',
          answer: 'Make sure you\'re logged in to your account and have a stable internet connection. Progress is saved automatically as you complete lessons. If you\'re still experiencing issues, please contact support@ai-mastery-hub.com.'
        }
      ]
    }
  ];

  const toggleCategory = (index: number) => {
    setExpandedCategory(expandedCategory === index ? null : index);
  };

  const toggleQuestion = (question: string) => {
    setExpandedQuestion(expandedQuestion === question ? null : question);
  };

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q =>
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Help Center - AI Mastery Hub | Frequently Asked Questions</title>
        <meta name="description" content="Find answers to common questions about AI Mastery Hub courses, billing, technical issues, and more." />
      </Helmet>

      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
            <span className="text-purple-400">💡 We're here to help</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
            <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Help Center
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Find answers to frequently asked questions and get the support you need
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/20 rounded-xl pl-12 pr-4 py-4 focus:outline-none focus:border-purple-500 transition-all text-lg"
            />
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg mb-6">No results found for "{searchTerm}"</p>
              <p className="text-gray-500">Try searching with different keywords or browse our categories below.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredCategories.map((category, categoryIndex) => (
                <div
                  key={categoryIndex}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 overflow-hidden"
                >
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(categoryIndex)}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-white/5 transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                        {category.icon}
                      </div>
                      <h2 className="text-2xl font-bold">{category.title}</h2>
                      <span className="text-sm text-gray-400">({category.questions.length})</span>
                    </div>
                    {expandedCategory === categoryIndex ? (
                      <ChevronUp className="w-6 h-6 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </button>

                  {/* Questions */}
                  {expandedCategory === categoryIndex && (
                    <div className="border-t border-white/10">
                      {category.questions.map((item, qIndex) => (
                        <div key={qIndex} className="border-b border-white/10 last:border-b-0">
                          <button
                            onClick={() => toggleQuestion(`${categoryIndex}-${qIndex}`)}
                            className="w-full px-8 py-5 flex items-center justify-between hover:bg-white/5 transition-all text-left"
                          >
                            <h3 className="text-lg font-semibold pr-4">{item.question}</h3>
                            {expandedQuestion === `${categoryIndex}-${qIndex}` ? (
                              <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                          {expandedQuestion === `${categoryIndex}-${qIndex}` && (
                            <div className="px-8 pb-6">
                              <p className="text-gray-400 leading-relaxed">{item.answer}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-900/10 via-black to-blue-900/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Still Need Help?</h2>
          <p className="text-xl text-gray-400 mb-10">
            Can't find what you're looking for? Our support team is ready to assist you.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Email Support */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-purple-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Mail className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Email Support</h3>
              <p className="text-gray-400 mb-6">
                Get help via email. General inquiries typically receive a response within 3-5 business days.
              </p>
              <a
                href="mailto:support@ai-mastery-hub.com"
                className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-3 rounded-full font-semibold hover:shadow-xl hover:shadow-purple-500/50 transition-all"
              >
                support@ai-mastery-hub.com
              </a>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-blue-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <MessageSquare className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Contact Form</h3>
              <p className="text-gray-400 mb-6">
                Prefer to use a contact form? Send us a detailed message.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white/10 border border-white/20 px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all"
              >
                Go to Contact Page
              </Link>
            </div>
          </div>

          <div className="mt-12 p-6 bg-blue-600/10 border border-blue-500/30 rounded-xl">
            <p className="text-sm text-blue-400">
              <strong>Note:</strong> We do not currently offer live chat support. For the fastest response,
              please email us at support@ai-mastery-hub.com or use our contact form.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
